import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Bot, User, X, ArrowRight } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import revenueOpsAvatar from "@/assets/revenue-ops-avatar.jpg";

export interface ServicesChatRef {
  openChat: () => void;
}

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  isTyping?: boolean;
  displayedContent?: string;
}

const ServicesChat = forwardRef<ServicesChatRef>((props, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isApplyPage = location.pathname === "/apply";

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content: "Welcome to the VGP Growth Framework™.\n\nWould you like to:",
      isUser: false,
      timestamp: new Date(),
      isTyping: true,
      displayedContent: ""
    }
  ]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showExitPanel, setShowExitPanel] = useState(false);
  const [homeFomoStep, setHomeFomoStep] = useState<'initial' | 'fomo' | 'curious'>('initial');
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    openChat: () => setIsOpen(true)
  }));

  // Listen for global openChatbot event
  useEffect(() => {
    const handleOpenChatbot = () => setIsOpen(true);
    window.addEventListener('openChatbot', handleOpenChatbot);
    return () => window.removeEventListener('openChatbot', handleOpenChatbot);
  }, []);

  const allServiceQuestions = [
    { text: "Review the OS Architecture", key: "os-architecture" },
    { text: "Explore the Four Pillars", key: "four-pillars" },
    { text: "Book Revenue Architecture Review", key: "booking" },
    { text: "Exit", key: "no-more-services" }
  ];

  const pricingQuestions = [
    { text: "Starter", key: "starter-plan" },
    { text: "Accelerator", key: "accelerator-plan" },
    { text: "Domination", key: "domination-plan" },
    { text: "Compare Plans", key: "compare-plans" },
    { text: "Book a Call", key: "book-call" }
  ];

  const compareQuestions = [
    { text: "Lead Volume", key: "compare-leads" },
    { text: "Ads Management", key: "compare-ads" },
    { text: "Automation/CRM", key: "compare-crm" },
    { text: "Reporting", key: "compare-reporting" },
    { text: "Dedicated Team", key: "compare-team" },
    { text: "Budget", key: "compare-budget" }
  ];

  const bookingQuestions = [
    { text: "This Week", key: "book-this-week" },
    { text: "Next Week", key: "book-next-week" },
    { text: "Share Availability", key: "book-availability" }
  ];

  const seoQuestions = [
    { text: "Google Maps SEO 📍", key: "google-maps-seo" },
    { text: "Website Optimization 💻", key: "website-optimization" },
    { text: "Social Media Optimization 📱", key: "social-media-optimization" },
    { text: "Pricing Plans 💳", key: "pricing" },
    { text: "Book a Call with Us 📆", key: "booking" }
  ];

  const seoServiceQuestions = [
    { text: "Show Pricing Plans 💳", key: "seo-pricing" },
    { text: "Book a Call 📆", key: "booking" },
    { text: "Back to SEO Services", key: "back-to-seo" },
    { text: "Back to Services", key: "back-to-main" }
  ];

  const funnelQuestions = [
    { text: "Funnel Strategy 🧭", key: "funnel-strategy" },
    { text: "Funnel Design & Build 🛠️", key: "funnel-design" },
    { text: "Funnel Automation ⚡", key: "funnel-automation" },
    { text: "Pricing Plans 💳", key: "pricing" },
    { text: "Book a Call with Us 📆", key: "booking" }
  ];

  const funnelServiceQuestions = [
    { text: "Show Pricing Plans 💳", key: "funnel-pricing" },
    { text: "Book a Call 📆", key: "booking" },
    { text: "Back to Sales Funnels", key: "back-to-funnels" },
    { text: "Back to Services", key: "back-to-main" }
  ];

  const automationQuestions = [
    { text: "Lead Capture & Follow-Up 📩", key: "automation-leads" },
    { text: "Appointment Booking 📆", key: "automation-booking" },
    { text: "Payment & Invoicing 💳", key: "automation-payments" },
    { text: "Custom Workflows 🔄", key: "automation-workflows" },
    { text: "Pricing Plans 💳", key: "pricing" },
    { text: "Book a Call with Us 📞", key: "booking" },
    { text: "Back to Services", key: "back-to-main" }
  ];

  const automationServiceQuestions = [
    { text: "Show Pricing Plans 💳", key: "automation-pricing" },
    { text: "Book a Call 📞", key: "booking" },
    { text: "Back to Sales Automation", key: "back-to-automation" },
    { text: "Back to Services", key: "back-to-main" }
  ];

  const contentQuestions = [
    { text: "Content Creation ✍️", key: "content-creation" },
    { text: "Social Media Content 📱", key: "content-social" },
    { text: "Blog & SEO Content 🌐", key: "content-blog" },
    { text: "Video & Creative Assets 🎥", key: "content-video" },
    { text: "Pricing Plans 💳", key: "pricing" },
    { text: "Book a Call with Us 📞", key: "booking" },
    { text: "Back to Services", key: "back-to-main" }
  ];

  const contentServiceQuestions = [
    { text: "Show Pricing Plans 💳", key: "content-pricing" },
    { text: "Book a Call 📞", key: "booking" },
    { text: "Back to Content Marketing", key: "back-to-content" },
    { text: "Back to Services", key: "back-to-main" }
  ];

  const getAvailableQuestions = () => {
    const baseQuestions = showFollowUp ? 
      allServiceQuestions.filter(q => !selectedServices.includes(q.key)) : 
      allServiceQuestions;
    
    return baseQuestions;
  };

  const [currentQuestionSet, setCurrentQuestionSet] = useState<'default' | 'pricing' | 'compare' | 'booking' | 'seo' | 'seo-service' | 'funnels' | 'funnel-service' | 'automation' | 'automation-service' | 'content' | 'content-service'>('default');

  const getCurrentQuestions = () => {
    switch (currentQuestionSet) {
      case 'pricing':
        return pricingQuestions;
      case 'compare':
        return compareQuestions;
      case 'booking':
        return bookingQuestions;
      case 'seo':
        return seoQuestions;
      case 'seo-service':
        return seoServiceQuestions;
      case 'funnels':
        return funnelQuestions;
      case 'funnel-service':
        return funnelServiceQuestions;
      case 'automation':
        return automationQuestions;
      case 'automation-service':
        return automationServiceQuestions;
      case 'content':
        return contentQuestions;
      case 'content-service':
        return contentServiceQuestions;
      default:
        return getAvailableQuestions();
    }
  };
    const generateResponse = (userMessage: string, serviceKey?: string): string => {
    const lowerMessage = userMessage.toLowerCase();
    const calendlyLink = "https://calendly.com/vincentgrowthpartners/book-a-free-strategy-call-today";
    
    // OS Architecture Review
    if (serviceKey === 'os-architecture') {
      return `The VGP Growth Framework™ is a bespoke framework built to replace fragmented outbound stacks with a single, automated pipeline engine.\n\nIt deploys across four integrated pillars:\n\n1. **Acquisition Infrastructure** — Paid systems driving qualified pipeline.\n2. **Conversion Infrastructure** — Funnel and website architecture built for ROI.\n3. **Pipeline Automation** — CRM, nurture and revenue operations.\n4. **Outbound Infrastructure** — LinkedIn SDR deployment for controlled growth.\n\n**Typical Deployment:** Infrastructure live within 14 days. Initial pipeline expansion within 30 days.\n\nWould you like to explore a specific pillar or book a Revenue Architecture Review?\n\n[CALENDLY_BUTTON]Book Revenue Architecture Review[/CALENDLY_BUTTON]`;
    }

    // Four Pillars Detail
    if (serviceKey === 'four-pillars') {
      return `**The Four Pillars of the Growth Framework™**\n\n**1. Acquisition Infrastructure**\nPaid advertising systems across Google, Meta and LinkedIn driving qualified B2B pipeline at scale.\n\n**2. Conversion Infrastructure**\nOptimised website and funnel architecture engineered for measurable ROI. Every page is a conversion asset.\n\n**3. Pipeline Automation**\nCRM integration, structured nurture sequences and revenue operations systems that eliminate manual follow-up.\n\n**4. Outbound Infrastructure (LinkedIn)**\nHyper-personalised LinkedIn SDR deployment for controlled outbound growth. Precision targeting, not spray-and-pray.\n\nEach pillar is deployed as part of a single, integrated system. Ready to see how it applies to your business?\n\n[CALENDLY_BUTTON]Book Revenue Architecture Review[/CALENDLY_BUTTON]`;
    }

    // VGP Pricing Plan Responses
    if (serviceKey === 'pricing') {
      return `VGP Plans\nPick the level that fits your growth goals:\n\n**Growth Starter, £1,500/mo**\nSEO audit & strategy, local SEO optimization, monthly reports, meta & Instagram ads.\n\n**Business Accelerator, £1,999/mo (Most Popular)**\nEverything in Starter + 2 optimized landing pages, CRM integration & optimization, Google & Facebook Ads, advanced tracking, dedicated account manager, bi-weekly strategy calls.\n\n**Enterprise, £5,000/mo**\nMulti-channel growth engine: LinkedIn/YouTube ads, video campaigns, competitor analysis, custom integrations, weekly strategy sessions, dedicated growth team.\n\nWhat do you want to explore?`;
    }

    // Individual Plan Responses
    if (serviceKey === 'starter-plan') {
      return `**Growth Starter, £1,500/mo**\n\nPerfect for businesses just getting started with digital marketing. Includes:\n• Complete SEO audit & strategy\n• Local SEO optimization\n• Meta & Instagram ads management\n• Monthly performance reports\n• Email support\n\nThis plan helps you establish your online presence and start generating consistent leads. Ready to get started?\n\n[CALENDLY_BUTTON]Book a Call[/CALENDLY_BUTTON]`;
    }

    if (serviceKey === 'accelerator-plan') {
      return `**Business Accelerator, £1,999/mo (Most Popular)**\n\nOur most popular plan for scaling businesses. Includes:\n• Everything in Starter Plan\n• 2 optimized landing pages\n• CRM integration & optimization\n• Google Ads & Facebook Ads management\n• Advanced conversion tracking & analytics\n• Dedicated account manager\n• Bi-weekly strategy calls\n• Priority support\n\nScale your business with proven systems that convert.\n\n[CALENDLY_BUTTON]Book a Strategy Call[/CALENDLY_BUTTON]`;
    }

    if (serviceKey === 'domination-plan') {
      return `**Enterprise, £5,000/mo**\n\nMulti-channel growth engine for ambitious businesses. Includes:\n• LinkedIn & YouTube advertising\n• Video campaign production\n• Competitor analysis & market insights\n• Custom integrations & workflows\n• Weekly strategy sessions\n• Dedicated growth team\n\nDominate your market with enterprise-level strategies.\n\n[CALENDLY_BUTTON]Let's Discuss Your Goals[/CALENDLY_BUTTON]`;
    }


    // Compare Plans Flow
    if (serviceKey === 'compare-plans') {
      return `Great, what matters most to you?`;
    }

     // Book call responses for specific plans
    if (serviceKey === 'book-call') {
      return `Great! Let's schedule a call to dive into your goals and show you how we can rank you higher on Google and drive real business growth.\n\n[CALENDLY_BUTTON]Book a Call with Us[/CALENDLY_BUTTON]`;
    }

    if (serviceKey === 'book-this-week') {
      return `Perfect! I have availability this week. Let's get you booked in:\n\n[CALENDLY_BUTTON]Book This Week[/CALENDLY_BUTTON]`;
    }

    if (serviceKey === 'book-next-week') {
      return `Great choice! Next week works well. Here's my calendar:\n\n[CALENDLY_BUTTON]Book Next Week[/CALENDLY_BUTTON]`;
    }

    if (serviceKey === 'book-availability') {
      return `I'd love to work around your schedule! Check my availability and pick a time that works best for you:\n\n[CALENDLY_BUTTON]View All Available Times[/CALENDLY_BUTTON]`;
    }
    if (serviceKey === 'compare-leads') {
      return `**Lead Volume Comparison:**\n\n• **Starter**: 20-50 qualified leads/month\n• **Accelerator**: 100-300 qualified leads/month\n• **Domination**: 500+ qualified leads/month\n• **Elite**: 1000+ leads/month across multiple territories\n\nNeed help choosing the right volume for your business?\n\n[CALENDLY_BUTTON]Book a Call[/CALENDLY_BUTTON]`;
    }

    if (serviceKey === 'compare-ads') {
      return `**Ads Management Comparison:**\n\n• **Starter**: No paid ads (organic only)\n• **Accelerator**: Google & Facebook Ads with optimization\n• **Domination**: Multi-platform ads + video campaigns\n• **Elite**: Cross-platform orchestration + custom creative\n\nReady to scale with ads?\n\n[CALENDLY_BUTTON]Let's Chat[/CALENDLY_BUTTON]`;
    }

    if (serviceKey === 'compare-crm') {
      return `**Automation/CRM Comparison:**\n\n• **Starter**: Basic tracking only\n• **Accelerator**: Full CRM + marketing automations\n• **Domination**: Advanced workflows + integrations\n• **Elite**: Custom automation systems\n\nAutomation can transform your business.\n\n[CALENDLY_BUTTON]Book a Strategy Call[/CALENDLY_BUTTON]`;
    }

    if (serviceKey === 'compare-reporting') {
      return `**Reporting Comparison:**\n\n• **Starter**: Monthly basic reports\n• **Accelerator**: Custom dashboard + bi-weekly calls\n• **Domination**: Real-time insights + weekly sessions\n• **Elite**: Enterprise reporting + quarterly reviews\n\nData-driven growth starts here.\n\n[CALENDLY_BUTTON]Book a Call[/CALENDLY_BUTTON]`;
    }

    if (serviceKey === 'compare-team') {
      return `**Team Support Comparison:**\n\n• **Starter**: Email support\n• **Accelerator**: Priority support + bi-weekly calls\n• **Domination**: Dedicated growth team + weekly sessions\n• **Elite**: Dedicated project manager + white-glove service\n\nExperience VGP's premium support.\n\n[CALENDLY_BUTTON]Book a Call[/CALENDLY_BUTTON]`;
    }

    if (serviceKey === 'compare-budget') {
      return `**Budget Comparison:**\n\n• **Growth Starter**: £1,500/mo - Perfect for getting started\n• **Business Accelerator**: £1,999/mo - Best ROI for scaling\n• **Enterprise**: £5,000/mo - Enterprise growth\n\nNot sure which fits your budget?\n\n[CALENDLY_BUTTON]Let's Discuss[/CALENDLY_BUTTON]`;
    }

    // SEO Services Flow
    if (serviceKey === 'google-maps-seo') {
      return `Perfect! Google Maps SEO helps local customers find you instantly when they search in your area.\nWe:\n✅ Optimize your business profile (photos, services, reviews)\n✅ Improve rankings in the Local 3-Pack\n✅ Drive calls, directions, and foot traffic\nWould you like to see how this fits into our plans?`;
    }

    if (serviceKey === 'website-optimization') {
      return `Strong SEO starts with your website. Our team ensures your site is:\n✅ Fast-loading & mobile-friendly\n✅ Keyword optimized for search visibility\n✅ Structured for maximum conversions\n✅ Enhanced with technical SEO (sitemaps, indexing, etc.)\nWant me to break down which plans include website optimization?`;
    }

    if (serviceKey === 'social-media-optimization') {
      return `Social profiles play a big role in SEO. We optimize your accounts to:\n✅ Align with your brand & keywords\n✅ Boost engagement that supports search rankings\n✅ Drive more clicks to your site & Google listing\nWould you like me to show how this scales with each plan?`;
    }

    // SEO-specific pricing
    if (serviceKey === 'seo-pricing') {
      return `Our SEO services scale based on your goals. Here's the breakdown:\n\n💡 Growth Starter (£1,500/mo)\n• Google Maps optimization\n• Basic website SEO fixes\n• Monthly performance report\n\n🚀 Business Accelerator (£1,999/mo) (most popular)\n• Full website SEO overhaul\n• Ongoing Google Maps ranking strategy\n• Social media SEO optimization\n• Bi-weekly reporting & insights\n\n👑 Enterprise (£5,000/mo)\n• Complete SEO + local domination strategy\n• Custom-built website optimization roadmap\n• Advanced Google Maps multi-location ranking\n• Social media authority growth\n• Weekly strategy calls + dedicated manager\n\nWould you like to book a call to find the best fit?`;
    }

    // Back to SEO menu
    if (serviceKey === 'back-to-seo') {
      return `At VGP, our SEO services are designed to make sure your business gets found — on Google Maps, search results, and across social platforms. 🚀\nWould you like me to walk you through what's included?`;
    }

    // Funnel Services Flow
    if (serviceKey === 'funnels') {
      return `Absolutely! At VGP, we design and build high-converting sales funnels that guide your prospects step-by-step from interest to purchase. 🎯\nWould you like me to show you what's included?`;
    }

    if (serviceKey === 'funnel-strategy') {
      return `We start with strategy:\n✅ Mapping your customer journey\n✅ Identifying high-converting offers\n✅ Choosing the right funnel type (lead gen, booking, sales, upsell)\nShall I show you how this connects to our plans?`;
    }

    if (serviceKey === 'funnel-design') {
      return `Our team builds sleek, conversion-focused funnels that:\n✅ Match your brand design\n✅ Guide customers with clear CTAs\n✅ Integrate payment, booking, or lead capture\nWant me to break down which plans cover funnel builds?`;
    }

    if (serviceKey === 'funnel-automation') {
      return `We don't just build funnels — we automate them so you can scale effortlessly:\n✅ Automated follow-up emails & SMS\n✅ Lead nurturing sequences\n✅ Calendar integrations for instant bookings\nWould you like me to show you which plans include automation?`;
    }

    // Funnel-specific pricing
    if (serviceKey === 'funnel-pricing') {
      return `Our funnel services are built into our growth packages:\n\n💡 Growth Starter (£1,500/mo)\n• Basic funnel setup (lead gen or booking)\n• Integration with your website\n• Simple follow-up automation\n\n🚀 Business Accelerator (£1,999/mo) (most popular)\n• Multi-step funnel build\n• Conversion-optimized landing pages\n• Email + SMS automation sequences\n• Monthly performance review\n\n👑 Enterprise (£5,000/mo)\n• Advanced funnel ecosystem (sales, upsell, cross-sell)\n• Full automation with CRM integration\n• Dedicated creative + strategy team\n• Weekly optimization calls\n\nWould you like to book a call to see which funnel type fits your business best?`;
    }

    // Back to funnel menu
    if (serviceKey === 'back-to-funnels') {
      return `Absolutely! At VGP, we design and build high-converting sales funnels that guide your prospects step-by-step from interest to purchase. 🎯\nWould you like me to show you what's included?`;
    }

    // Automation Services Flow
    if (serviceKey === 'automation') {
      return `Yes — we can automate your entire sales process from first click to closed deal. ⚡\nOur goal is simple: cut out manual tasks and let your business scale while you focus on growth.\nWould you like to see what we can automate?`;
    }

    if (serviceKey === 'automation-leads') {
      return `We make sure no lead ever slips through the cracks:\n✅ Automated forms + chatbots to collect details\n✅ Instant SMS/email follow-ups\n✅ Lead nurturing sequences to warm prospects until they're ready to buy\nWould you like to see how this fits into our plans?`;
    }

    if (serviceKey === 'automation-booking') {
      return `Forget the back-and-forth. We:\n✅ Sync calendars to auto-book calls\n✅ Send reminders to cut no-shows\n✅ Route calls to the right team member automatically\nWant me to break down which packages cover appointment automations?`;
    }

    if (serviceKey === 'automation-payments') {
      return `We can even automate revenue collection:\n✅ Auto-generate invoices\n✅ Stripe/PayPal integrations for instant payments\n✅ Subscription or retainer billing on autopilot\nWould you like me to show which plans include payment automation?`;
    }

    if (serviceKey === 'automation-workflows') {
      return `Every business is unique, that's why we build custom automations, such as:\n✅ CRM updates & task assignments\n✅ Follow-up sequences triggered by client actions\n✅ Advanced reporting dashboards\nIf you can think it, we can automate it. Shall I show you how we package this?`;
    }

    // Automation-specific pricing
    if (serviceKey === 'automation-pricing') {
      return `Our automation services scale with your needs:\n\n💡 Growth Starter (£1,500/mo)\n• Lead capture form automation\n• Simple follow-up workflows\n• Appointment booking setup\n\n🚀 Business Accelerator (£1,999/mo) (most popular)\n• Multi-step lead nurturing\n• Full appointment system with reminders\n• Payment automation\n• Reporting dashboards\n\n👑 Enterprise (£5,000/mo)\n• Complete end-to-end automation\n• Custom workflows for sales, marketing, and operations\n• Advanced integrations with your existing tools\n• Dedicated automation strategist\n\nWould you like to book a call so we can design your perfect system?`;
    }

    // Back to automation menu
    if (serviceKey === 'back-to-automation') {
      return `Yes — we can automate your entire sales process from first click to closed deal. ⚡\nOur goal is simple: cut out manual tasks and let your business scale while you focus on growth.\nWould you like to see what we can automate?`;
    }

    // Content Marketing Services Flow
    if (serviceKey === 'content') {
      return `Great question! Content marketing is all about creating and sharing valuable content that attracts your ideal customers and builds trust — so when they're ready to buy, they choose you. 📈\nWant me to show you what we do for clients?`;
    }

    if (serviceKey === 'content-creation') {
      return `We create content that converts, including:\n✅ Social posts, blogs, and case studies\n✅ Marketing emails that drive action\n✅ Graphics, ads, and creatives aligned to your brand\nWould you like me to show how this fits into our packages?`;
    }

    if (serviceKey === 'content-social') {
      return `Your social channels become lead machines when managed correctly. We:\n✅ Plan your content calendar\n✅ Create scroll-stopping posts and reels\n✅ Optimize for engagement and conversions\nWant me to show you which plans cover social media content?`;
    }

    if (serviceKey === 'content-blog') {
      return `Blogs aren't just for writing — they're SEO engines. We:\n✅ Write keyword-optimized blog posts\n✅ Boost search rankings & authority\n✅ Position your business as the go-to expert\nWould you like me to show which plans include blogging?`;
    }

    if (serviceKey === 'content-video') {
      return `We make your content stand out with video and creative design:\n✅ Short-form videos for social & ads\n✅ Branded graphics and carousels\n✅ Case study & testimonial videos\nWant me to show how this scales in our plans?`;
    }

    // Content-specific pricing
    if (serviceKey === 'content-pricing') {
      return `Our content marketing services are included in our packages:\n\n💡 Growth Starter (£1,500/mo)\n• 8 to 10 branded social posts per month\n• 1 to 2 blog articles\n• Basic creative support\n\n🚀 Business Accelerator (£1,999/mo) (most popular)\n• 20+ branded social posts (with captions & hashtags)\n• Weekly blog articles with SEO focus\n• Video/reel production\n• Monthly reporting\n\n👑 Enterprise (£5,000/mo)\n• Daily content across multiple channels\n• Advanced video & creative campaigns\n• Full SEO blog strategy & authority building\n• Dedicated creative team\n\nWould you like me to book a call to find the right plan for your business?`;
    }

    // Back to content menu
    if (serviceKey === 'back-to-content') {
      return `Great question! Content marketing is all about creating and sharing valuable content that attracts your ideal customers and builds trust, so when they're ready to buy, they choose you. 📈\nWant me to show you what we do for clients?`;
    }

    // Fail-safe for unsure users
    if (lowerMessage.includes('not sure') || lowerMessage.includes('unsure') || lowerMessage.includes('help me choose')) {
      return `Not sure where to start? Tell me your monthly lead target and budget, and I'll recommend a plan.\n\n[CALENDLY_BUTTON]Book a Free Strategy Call[/CALENDLY_BUTTON]\n\nWe'll analyze your business together.`;
    }
    // Service-specific responses - all lead to booking
    if (lowerMessage.includes('seo') || lowerMessage.includes('search')) {
      return `At VGP, our SEO services are designed to make sure your business gets found, on Google Maps, search results, and across social platforms. 🚀\nWould you like me to walk you through what's included?`;
    }
    
    if (lowerMessage.includes('ads') || lowerMessage.includes('advertising') || lowerMessage.includes('paid')) {
      return `Paid advertising is one of our specialties! We maximize ROI with data-driven Google Ads, Facebook Ads, and LinkedIn campaigns that convert prospects into customers. I'd love to show you exactly how we can scale your business.\n\n[CALENDLY_BUTTON]Book a Call with Us[/CALENDLY_BUTTON]`;
    }
    
    if (lowerMessage.includes('content') || lowerMessage.includes('blog') || lowerMessage.includes('social')) {
      return `Great question! Content marketing is all about creating and sharing valuable content that attracts your ideal customers and builds trust, so when they're ready to buy, they choose you. 📈\nWant me to show you what we do for clients?`;
    }
    
    if (lowerMessage.includes('funnel') || lowerMessage.includes('landing') || lowerMessage.includes('conversion')) {
      return `Absolutely! At VGP, we design and build high-converting sales funnels that guide your prospects step-by-step from interest to purchase. 🎯\nWould you like me to show you what's included?`;
    }
    
    if (lowerMessage.includes('crm') || lowerMessage.includes('automation') || lowerMessage.includes('sales')) {
      return `Yes — we can automate your entire sales process from first click to closed deal. ⚡\nOur goal is simple: cut out manual tasks and let your business scale while you focus on growth.\nWould you like to see what we can automate?`;
    }
    
    // Pricing responses
    if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('pricing')) {
      return `**Growth Starter**, £1,500/mo\n**Business Accelerator**, £1,999/mo (Most Popular)\n**Enterprise**, £5,000/mo`;
    }
    
    if (lowerMessage.includes('pricing page') || lowerMessage.includes('view pricing')) {
      return `You can view our pricing information at /pricing. This page contains detailed information about all our service packages and pricing tiers.`;
    }
    
    if (lowerMessage.includes('time') || lowerMessage.includes('long') || lowerMessage.includes('results')) {
      return `Timeline depends on your goals and current situation. Some strategies show results immediately, others build over time.\n\n[CALENDLY_BUTTON]Let's Discuss Your Timeline[/CALENDLY_BUTTON]`;
    }
    
    // Greetings
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      return `Hello! I'm here to help you grow your business with our digital marketing services. The best way to see how we can help is on a call with us.\n\n[CALENDLY_BUTTON]Book a Call with Us[/CALENDLY_BUTTON]`;
    }
    
    // General help
    if (lowerMessage.includes('help') || lowerMessage.includes('services') || lowerMessage.includes('what')) {
      return `I can tell you about our services, but the real magic happens when we analyze YOUR specific business on a call with us. It's free and we'll create a custom growth plan.\n\n[CALENDLY_BUTTON]Book a Call with Us[/CALENDLY_BUTTON]`;
    }
    
    // Exit/Close chat response - triggers structured exit panel
    if (serviceKey === 'no-more-services') {
      return `Understood.\n\nIf you'd prefer to explore further before speaking with us, you can:`;
    }
    
    // Default response
    return `Thanks for reaching out! We specialize in growing businesses through SEO, paid advertising, content marketing, sales funnels, and CRM automation. The best way to see how we can help YOUR business is on a call with us.\n\n[CALENDLY_BUTTON]Book a Call with Us[/CALENDLY_BUTTON]`;
  };
  
  // Function to render message content with buttons
  const renderMessageContent = (content: string, isTyping: boolean, displayedContent?: string) => {
    const textToRender = isTyping ? displayedContent : content;
    if (!textToRender) return null;
    
    // Check for button patterns
    const calendlyButtonPattern = /\[CALENDLY_BUTTON\](.*?)\[\/CALENDLY_BUTTON\]/g;
    const linkedinButtonPattern = /\[LINKEDIN_BUTTON\](.*?)\[\/LINKEDIN_BUTTON\]/g;
    
    // First handle Calendly buttons
    let parts = textToRender.split(calendlyButtonPattern);
    
    if (parts.length === 1 && !textToRender.includes('[LINKEDIN_BUTTON]')) {
      // No buttons found, render as normal text
      return <p className="text-sm break-words whitespace-pre-wrap">{textToRender}</p>;
    }
    
    // Handle LinkedIn buttons separately
    if (textToRender.includes('[LINKEDIN_BUTTON]')) {
      const linkedinParts = textToRender.split(linkedinButtonPattern);
      return (
        <div className="space-y-3">
          {linkedinParts.map((part, index) => {
            if (index % 2 === 0) {
              // Check if this text part contains Calendly buttons
              const calendlyParts = part.split(calendlyButtonPattern);
              if (calendlyParts.length > 1) {
                return calendlyParts.map((calPart, calIndex) => {
                  if (calIndex % 2 === 0) {
                    return calPart.trim() ? (
                      <p key={`${index}-${calIndex}`} className="text-sm break-words whitespace-pre-wrap">
                        {calPart}
                      </p>
                    ) : null;
                  } else {
                    return (
                      <Button
                        key={`${index}-${calIndex}`}
                        onClick={() => window.open("https://calendly.com/vincentgrowthpartners/book-a-free-strategy-call-today", "_blank")}
                        className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
                      >
                        {calPart}
                      </Button>
                    );
                  }
                });
              }
              return part.trim() ? (
                <p key={index} className="text-sm break-words whitespace-pre-wrap">
                  {part}
                </p>
              ) : null;
            } else {
              return (
                <Button
                  key={index}
                  onClick={() => window.open("https://www.linkedin.com/in/vincentgrowthpartners", "_blank")}
                  className="w-full bg-[#0A66C2] hover:bg-[#094c93] text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  {part}
                </Button>
              );
            }
          })}
        </div>
      );
    }
    
    // Render text with Calendly buttons only
    return (
      <div className="space-y-3">
        {parts.map((part, index) => {
          // Even indices are text, odd indices are button text
          if (index % 2 === 0) {
            return part.trim() ? (
              <p key={index} className="text-sm break-words whitespace-pre-wrap">
                {part}
              </p>
            ) : null;
          } else {
            return (
              <Button
                key={index}
                onClick={() => window.open("https://calendly.com/vincentgrowthpartners/book-a-free-strategy-call-today", "_blank")}
                className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
              >
                {part}
              </Button>
            );
          }
        })}
      </div>
    );
  };
  
  const typeWriterEffect = (messageId: string, fullText: string, onComplete?: () => void) => {
    let currentIndex = 0;
    const typeInterval = setInterval(() => {
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, displayedContent: fullText.slice(0, currentIndex + 1) }
          : msg
      ));
      
      currentIndex++;
      if (currentIndex >= fullText.length) {
        clearInterval(typeInterval);
        // Mark typing as complete
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, isTyping: false }
            : msg
        ));
        // Call the completion callback if provided
        if (onComplete) {
          setTimeout(onComplete, 300); // Small delay before follow-up
        }
      }
      
      // Auto-scroll during typing
      setTimeout(scrollToBottom, 10);
    }, 13); // Faster typing speed (1.5x faster)
  };
  const handleSuggestionClick = (suggestion: { text: string; key: string }) => {
    // Special handling for "Back to Menu" - just reset to main services
    if (suggestion.key === 'back-to-main') {
      setCurrentQuestionSet('default');
      setShowSuggestions(true);
      return;
    }

    // Handle close chat specially
    if (suggestion.key === 'close-chat') {
      const userMessage: Message = {
        id: Date.now().toString(),
        content: suggestion.text,
        isUser: true,
        timestamp: new Date(),
        displayedContent: suggestion.text
      };

      // Add user message immediately
      setMessages(prev => [...prev, userMessage]);
      setShowSuggestions(false);
      setIsTyping(true);
      
      // Show typing indicator for 3 seconds, then add bot response and close
      setTimeout(() => {
        setIsTyping(false);
        
        const botResponseContent = generateResponse(suggestion.text, suggestion.key);
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          content: botResponseContent,
          isUser: false,
          timestamp: new Date(),
          isTyping: true,
          displayedContent: ""
        };

        setMessages(prev => [...prev, botResponse]);
        
        // Start typewriter effect and close chat after completion
        setTimeout(() => {
          typeWriterEffect(botResponse.id, botResponseContent, () => {
            // Close chat after message finishes typing
            setTimeout(() => {
              setIsOpen(false);
            }, 1500);
          });
        }, 100);
      }, 1500);
      return;
    }

    // Handle pricing flow
    if (suggestion.key === 'pricing') {
      setCurrentQuestionSet('pricing');
    } else if (['starter-plan', 'accelerator-plan', 'domination-plan', 'elite-plan'].includes(suggestion.key)) {
      setCurrentQuestionSet('default');
    } else if (suggestion.key === 'compare-plans') {
      setCurrentQuestionSet('compare');
    } else if (['compare-leads', 'compare-ads', 'compare-crm', 'compare-reporting', 'compare-team', 'compare-budget'].includes(suggestion.key)) {
      setCurrentQuestionSet('default');
    } else if (suggestion.key === 'book-call') {
      setCurrentQuestionSet('booking');
    } else if (['book-this-week', 'book-next-week', 'book-availability'].includes(suggestion.key)) {
      setCurrentQuestionSet('default');
    }

    // Handle SEO flow
    if (suggestion.key === 'seo') {
      setCurrentQuestionSet('seo');
    } else if (['google-maps-seo', 'website-optimization', 'social-media-optimization'].includes(suggestion.key)) {
      setCurrentQuestionSet('seo-service');
    } else if (suggestion.key === 'seo-pricing') {
      setCurrentQuestionSet('seo-service');
    } else if (suggestion.key === 'back-to-seo') {
      setCurrentQuestionSet('seo');
    }

    // Handle funnel flow
    if (suggestion.key === 'funnels') {
      setCurrentQuestionSet('funnels');
    } else if (['funnel-strategy', 'funnel-design', 'funnel-automation'].includes(suggestion.key)) {
      setCurrentQuestionSet('funnel-service');
    } else if (suggestion.key === 'funnel-pricing') {
      setCurrentQuestionSet('funnel-service');
    } else if (suggestion.key === 'back-to-funnels') {
      setCurrentQuestionSet('funnels');
    }

    // Handle automation flow
    if (suggestion.key === 'automation') {
      setCurrentQuestionSet('automation');
    } else if (['automation-leads', 'automation-booking', 'automation-payments', 'automation-workflows'].includes(suggestion.key)) {
      setCurrentQuestionSet('automation-service');
    } else if (suggestion.key === 'automation-pricing') {
      setCurrentQuestionSet('automation-service');
    } else if (suggestion.key === 'back-to-automation') {
      setCurrentQuestionSet('automation');
    }

    // Handle content flow
    if (suggestion.key === 'content') {
      setCurrentQuestionSet('content');
    } else if (['content-creation', 'content-social', 'content-blog', 'content-video'].includes(suggestion.key)) {
      setCurrentQuestionSet('content-service');
    } else if (suggestion.key === 'content-pricing') {
      setCurrentQuestionSet('content-service');
    } else if (suggestion.key === 'back-to-content') {
      setCurrentQuestionSet('content');
    }

    // Track selected service immediately
    if (!['pricing', 'booking', 'pricing-page', 'no-more-services', 'close-chat'].includes(suggestion.key) && !selectedServices.includes(suggestion.key)) {
      setSelectedServices(prev => [...prev, suggestion.key]);
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      content: suggestion.text,
      isUser: true,
      timestamp: new Date(),
      displayedContent: suggestion.text
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setShowSuggestions(false);
    setIsTyping(true);
    
    // Show typing indicator for 3 seconds, then add bot response
    setTimeout(() => {
      setIsTyping(false);
      
      const botResponseContent = generateResponse(suggestion.text, suggestion.key);
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: botResponseContent,
        isUser: false,
        timestamp: new Date(),
        isTyping: true,
        displayedContent: ""
      };

      // Don't show follow-up message for certain interactions including SEO and funnel flows
      const seoFlowKeys = ['seo', 'google-maps-seo', 'website-optimization', 'social-media-optimization', 'seo-pricing', 'back-to-seo'];
      const funnelFlowKeys = ['funnels', 'funnel-strategy', 'funnel-design', 'funnel-automation', 'funnel-pricing', 'back-to-funnels'];
      const automationFlowKeys = ['automation', 'automation-leads', 'automation-booking', 'automation-payments', 'automation-workflows', 'automation-pricing', 'back-to-automation'];
      const contentFlowKeys = ['content', 'content-creation', 'content-social', 'content-blog', 'content-video', 'content-pricing', 'back-to-content'];
      const shouldShowFollowUp = !['pricing-page', 'no-more-services', 'close-chat', 'pricing', 'compare-plans', 'book-call', ...seoFlowKeys, ...funnelFlowKeys, ...automationFlowKeys, ...contentFlowKeys].includes(suggestion.key);
      const followUpContent = "Is there anything else VGP can help you with today, we also offer...";

      // Only add the bot response initially
      setMessages(prev => [...prev, botResponse]);
      
      // Start typewriter effect for bot response
      setTimeout(() => {
        // Special handling for no-more-services - close chat after typing
        if (suggestion.key === 'no-more-services') {
          typeWriterEffect(botResponse.id, botResponseContent, () => {
            // Show structured exit panel instead of closing
            setTimeout(() => {
              setShowExitPanel(true);
              setTimeout(scrollToBottom, 100);
            }, 500);
          });
        } else {
          typeWriterEffect(botResponse.id, botResponseContent, shouldShowFollowUp ? () => {
            // Show typing indicator for follow-up message first
            setIsTyping(true);
            
            setTimeout(() => {
              // Stop typing indicator and add follow-up message
              setIsTyping(false);
              
              const followUpMessage: Message = {
                id: (Date.now() + 2).toString(),
                content: followUpContent,
                isUser: false,
                timestamp: new Date(),
                isTyping: true,
                displayedContent: ""
              };
              
              setMessages(prev => [...prev, followUpMessage]);
              
              // Start typewriter for follow-up message immediately
              setTimeout(() => {
                typeWriterEffect(followUpMessage.id, followUpContent, () => {
                  // Show suggestions after follow-up is complete
                  setTimeout(() => {
                    setShowSuggestions(true);
                    setTimeout(scrollToBottom, 100);
                  }, 500);
                });
              }, 100);
            }, 1000); // Show typing bubbles for 1 second
          } : () => {
            // No follow-up, just show suggestions after main response
            setTimeout(() => {
              setShowSuggestions(true);
              setTimeout(scrollToBottom, 100);
            }, 500);
          });
        }
      }, 100);
      
      // Scroll after adding messages
      setTimeout(scrollToBottom, 50);
      
      if (shouldShowFollowUp) {
        setShowFollowUp(true);
      }
    }, 1500);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      isUser: true,
      timestamp: new Date(),
      displayedContent: inputValue
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    const messageContent = inputValue;
    setInputValue("");
    setShowSuggestions(false);
    setIsTyping(true);
    
    // Show typing indicator for 3 seconds, then add bot response
    setTimeout(() => {
      setIsTyping(false);
      
      const botResponseContent = generateResponse(messageContent);
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: botResponseContent,
        isUser: false,
        timestamp: new Date(),
        isTyping: true,
        displayedContent: ""
      };

      // Don't show follow-up message for thank you responses
      const shouldShowFollowUp = !inputValue.toLowerCase().includes("don't need") && !inputValue.toLowerCase().includes('no more') && !inputValue.toLowerCase().includes('thank you');
      const followUpContent = "Is there anything else VGP can help you with today, we also offer...";

      setMessages(prev => [...prev, botResponse]);
      
      setTimeout(() => {
        typeWriterEffect(botResponse.id, botResponseContent, () => {
          // Show typing indicator for follow-up message first
          setIsTyping(true);
          
          setTimeout(() => {
            // Stop typing indicator and add follow-up message
            setIsTyping(false);
            
            const followUpMessage: Message = {
              id: (Date.now() + 2).toString(),
              content: followUpContent,
              isUser: false,
              timestamp: new Date(),
              isTyping: true,
              displayedContent: ""
            };
            
            setMessages(prev => [...prev, followUpMessage]);
            
            // Start typewriter for follow-up message immediately
            setTimeout(() => {
              typeWriterEffect(followUpMessage.id, followUpContent, () => {
                // Show suggestions after follow-up is complete
                setTimeout(() => {
                  setShowSuggestions(true);
                  setTimeout(scrollToBottom, 100);
                }, 500);
              });
            }, 100);
          }, 1000); // Show typing bubbles for 1 second
        });
      }, 100);
      
      // Scroll after adding messages
      setTimeout(scrollToBottom, 50);
    }, 1500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  // Smooth auto-scroll to bottom when messages change - wrapped in rAF to prevent forced reflow
  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollAreaRef.current) {
        const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (scrollElement) {
          scrollElement.scrollTo({
            top: scrollElement.scrollHeight,
            behavior: 'smooth'
          });
        }
      }
    });
  };

  // Auto-scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll when suggestions change
  useEffect(() => {
    if (showSuggestions) {
      setTimeout(scrollToBottom, 100);
    }
  }, [showSuggestions]);

  // Handle initial welcome message animation when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 1 && messages[0].isTyping) {
      // Wait 1.5 seconds, then start typing the welcome message
      setTimeout(() => {
        const welcomeMessage = messages[0];
        typeWriterEffect(welcomeMessage.id, welcomeMessage.content, () => {
          // After welcome message finishes, show service suggestions
          setTimeout(() => {
            setShowSuggestions(true);
          }, 1000);
        });
      }, 1500);
    }
  }, [isOpen]);

  // Auto-expand when user starts typing anywhere on the page
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Open chat if user types and chat is closed (exclude special keys)
      if (!isOpen && 
          !e.metaKey && !e.ctrlKey && !e.altKey && 
          e.key.length === 1 && 
          !['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'].includes(e.key)) {
        setIsOpen(true);
        // Small delay to ensure chat is open before focusing input
        setTimeout(() => {
          const input = document.querySelector('[data-chat-input]') as HTMLInputElement;
          if (input) {
            input.focus();
            input.value = e.key;
            setInputValue(e.key);
          }
        }, 100);
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => document.removeEventListener('keypress', handleKeyPress);
  }, [isOpen]);

  // Reusable avatar toggle button
  const AvatarToggle = ({ onClick }: { onClick: () => void }) => (
    <div className="fixed bottom-24 right-6 z-50 flex flex-col items-center gap-2">
      <button
        onClick={onClick}
        className="relative w-14 h-14 rounded-full overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-secondary/50"
        aria-label="Open Revenue Operations Desk"
      >
        {/* Subtle breathing gold halo */}
        <span className="absolute inset-0 rounded-full animate-[breathe_4s_ease-in-out_infinite] border-2 border-secondary/40" aria-hidden="true" />
        <img src={revenueOpsAvatar} alt="VGP Revenue Operations" className="w-full h-full object-cover" />
        {/* Online indicator */}
        <span className="absolute bottom-0.5 right-0.5 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-50" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500 border-2 border-background" />
        </span>
      </button>
      <span className="text-[10px] text-muted-foreground font-medium tracking-wide">Revenue Ops</span>
    </div>
  );

  // On apply/funnel page: show a simplified chatbot
  if (isApplyPage) {
    if (!isOpen) {
      return <AvatarToggle onClick={() => setIsOpen(true)} />;
    }

    return (
      <div className="fixed bottom-20 right-2 left-2 sm:left-auto sm:right-4 z-50 sm:max-w-sm sm:w-full">
        <Card className="flex flex-col shadow-luxury border border-border/50 bg-background">
          <CardHeader className="bg-gradient-to-r from-primary to-primary/90 text-white p-4 rounded-t-lg flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={revenueOpsAvatar} alt="VGP" className="w-8 h-8 rounded-full object-cover border border-white/20" />
                <div>
                  <CardTitle className="text-sm font-semibold">Revenue Operations Desk</CardTitle>
                  <p className="text-[10px] text-white/70 font-medium">VGP Client Infrastructure</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setIsOpen(false); setHomeFomoStep('initial'); }}
                className="text-white/70 hover:text-white hover:bg-white/10 h-7 w-7 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            {homeFomoStep === 'initial' && (
              <>
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">
                    Explore the VGP Growth Framework™
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Understand how we engineer predictable B2B pipeline growth through infrastructure, automation and structured deployment.
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => navigate("/about")}
                    variant="outline"
                    className="w-full justify-between text-left h-auto py-3 bg-background hover:bg-muted text-foreground border-border text-sm"
                  >
                    View The Framework
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </Button>
                  <Button
                    onClick={() => setHomeFomoStep('fomo')}
                    className="w-full justify-between text-left h-auto py-3 bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 text-sm"
                  >
                    Book a Strategy Call
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
            {homeFomoStep === 'fomo' && (
              <>
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">
                    Performance Snapshot
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    A single campaign deployment generated <span className="font-semibold text-secondary">57 qualified strategy meetings in 5 weeks</span> using the VGP Growth Framework™.
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-2">
                    We onboard a limited number of clients per quarter to maintain delivery quality.
                  </p>
                </div>
                <div className="flex flex-col gap-2 pt-1">
                  <Button
                    onClick={() => window.open("https://calendly.com/vincentgrowthpartners/book-a-free-strategy-call-today", "_blank")}
                    className="w-full justify-between h-auto py-3 bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 font-semibold text-sm"
                  >
                    Book Strategy Call
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => setHomeFomoStep('curious')}
                    variant="outline"
                    className="w-full justify-between text-left h-auto py-3 bg-background hover:bg-muted text-foreground border-border text-sm"
                  >
                    See The Deployment Process
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </Button>
                  <Button
                    onClick={() => setHomeFomoStep('initial')}
                    variant="ghost"
                    className="w-full justify-center h-auto py-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    ← Back
                  </Button>
                </div>
              </>
            )}
            {homeFomoStep === 'curious' && (
              <>
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">
                    How The System Works
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    We deployed a structured outbound acquisition system: automated multi-channel outreach, qualified lead routing straight into their calendar, and pipeline infrastructure that compounds.
                  </p>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                    <span className="font-semibold text-secondary">57 meetings. 5 weeks. Zero cold calling.</span>
                  </p>
                </div>
                <div className="flex flex-col gap-2 pt-1">
                  <Button
                    onClick={() => window.open("https://calendly.com/vincentgrowthpartners/book-a-free-strategy-call-today", "_blank")}
                    className="w-full justify-between h-auto py-3 bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 font-semibold text-sm"
                  >
                    Book Strategy Call
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => navigate("/about")}
                    variant="outline"
                    className="w-full justify-between h-auto py-3 bg-background hover:bg-muted text-foreground border-border text-sm"
                  >
                    Explore The Framework
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <AvatarToggle onClick={() => {
        setSelectedServices([]);
        setShowFollowUp(false);
        setShowExitPanel(false);
        setCurrentQuestionSet('default');
        setShowSuggestions(false);
        setMessages([{
          id: "1",
          content: "Welcome to the VGP Growth Framework™.\n\nWould you like to:",
          isUser: false,
          timestamp: new Date(),
          isTyping: true,
          displayedContent: ""
        }]);
        setIsOpen(true);
      }} />
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md w-full">
      <Card className="h-[50vh] md:h-[60vh] lg:h-[65vh] max-h-[800px] min-h-[400px] flex flex-col shadow-luxury border border-border/50 bg-background">
        <CardHeader className="bg-gradient-to-r from-primary to-primary/90 text-white p-4 rounded-t-lg flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={revenueOpsAvatar} alt="VGP" className="w-8 h-8 rounded-full object-cover border border-white/20" />
              <div>
                <CardTitle className="text-sm font-semibold">Revenue Operations Desk</CardTitle>
                <p className="text-[10px] text-white/70 font-medium">VGP Client Infrastructure</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white hover:bg-white/10 h-7 w-7 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col p-0 min-h-0">
          <ScrollArea className="flex-1 p-4 min-h-0" ref={scrollAreaRef}>
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-2 ${message.isUser ? 'justify-end' : 'justify-start'}`}
                >
                  {!message.isUser && (
                    <img src={revenueOpsAvatar} alt="VGP" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  )}
                  <div
                    className={`max-w-[70%] p-3 rounded-lg break-words ${
                      message.isUser
                        ? 'bg-primary text-white ml-auto'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    {renderMessageContent(message.content, message.isTyping, message.displayedContent)}
                    {message.isTyping && (
                      <span className="inline-block w-2 h-5 bg-current animate-pulse ml-1">|</span>
                    )}
                  </div>
                  {message.isUser && (
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
              ))}
              
              {isTyping && (
                <div className="flex gap-2 justify-start">
                  <img src={revenueOpsAvatar} alt="VGP" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  <div className="max-w-[70%] p-3 rounded-lg bg-muted text-foreground">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              
              {showSuggestions && !showExitPanel && (
                <div className="space-y-2 mt-4">
                  <p className="text-sm text-muted-foreground font-medium">
                    Select an option
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    {getCurrentQuestions().map((question, index) => (
                      <Button
                        key={`${question.key}-${index}`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleSuggestionClick(question)}
                        className="text-left justify-start h-auto p-2 whitespace-normal bg-background hover:bg-muted text-foreground border-border"
                      >
                        {question.text}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Structured Exit Panel */}
              {showExitPanel && (
                <div className="space-y-3 mt-4">
                  {/* LinkedIn */}
                  <Button
                    onClick={() => window.open("https://www.linkedin.com/in/vincentgrowthpartners", "_blank")}
                    className="w-full h-auto py-3 px-4 bg-[#0A66C2] hover:bg-[#094c93] text-white font-semibold text-sm flex flex-col items-start gap-1"
                  >
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                      Connect on LinkedIn
                    </span>
                    <span className="text-xs font-normal text-white/70 text-left">
                      Follow Evan Vincent for ongoing insights on B2B growth infrastructure.
                    </span>
                  </Button>

                  {/* YouTube */}
                  <Button
                    onClick={() => window.open("https://www.youtube.com/@vgpgrowthhub/shorts", "_blank")}
                    variant="outline"
                    className="w-full h-auto py-3 px-4 border-border bg-background hover:bg-muted text-foreground font-semibold text-sm flex flex-col items-start gap-1"
                  >
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                      Watch the Growth Framework™
                    </span>
                    <span className="text-xs font-normal text-muted-foreground text-left">
                      See how the VGP system engineers predictable pipeline growth.
                    </span>
                  </Button>

                  {/* Close - Tertiary */}
                  <Button
                    onClick={() => {
                      setShowExitPanel(false);
                      setIsOpen(false);
                    }}
                    variant="ghost"
                    className="w-full h-auto py-2 text-xs text-muted-foreground bg-muted/50 hover:bg-muted font-medium"
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
          
          {/* Input area */}
          <div className="p-3 border-t border-border flex gap-2 flex-shrink-0">
            <Input
              data-chat-input
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              className="flex-1 bg-muted border-border text-foreground placeholder:text-muted-foreground"
            />
            <Button
              onClick={handleSendMessage}
              size="icon"
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground shrink-0"
              disabled={!inputValue.trim()}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

ServicesChat.displayName = "ServicesChat";

export default ServicesChat;